// Entry card below the items list for requesting an order cancellation.
// Shows one of three states (resolved server-side as `order.cancelEntry`):
//   - 'request':    the Request cancellation button
//   - 'on-its-way': out-for-delivery call-only fallback copy + Call branch
//   - 'no-email':   no contact email call-only fallback copy + Call branch
// Ported from octopus-mock/components/orders/cancel-request.tsx (CancelOrderSection).
import { Phone } from 'lucide-react'
import { Button } from '../../radix'

export type CancelEntry = 'request' | 'on-its-way' | 'no-email'

interface CancelOrderSectionProps {
  entry: CancelEntry
  branchPhone: string
  onRequest?: () => void
}

export function CancelOrderSection({ entry, branchPhone, onRequest }: CancelOrderSectionProps) {
  return (
    <section
      aria-labelledby="cancel-order"
      className="mt-3 sm:mt-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
    >
      <div className="bg-gray-50 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200">
        <h2 id="cancel-order" className="text-gray-900 font-bold text-xs sm:text-sm">Cancel order</h2>
      </div>
      <div className="px-3 sm:px-4 py-3 space-y-3 text-sm text-gray-700">
        {entry === 'request' && (
          <>
            <p>We&apos;ll email the branch to ask them to cancel it. It&apos;s only cancelled once they confirm.</p>
            <Button variant="outline" className="min-h-[44px] w-full" onClick={onRequest}>
              Request cancellation
            </Button>
          </>
        )}
        {entry === 'on-its-way' && (
          <p>It&apos;s already on its way, so it can&apos;t be cancelled by email. Call the branch to sort it out.</p>
        )}
        {entry === 'no-email' && (
          <p>We don&apos;t have an email address for this branch. Call them to cancel.</p>
        )}
        {entry !== 'request' && branchPhone && (
          <Button asChild variant="outline" className="min-h-[44px] w-full">
            <a href={`tel:${branchPhone}`}>
              <Phone className="w-4 h-4" />
              Call branch
              <span className="font-normal tabular-nums">{branchPhone}</span>
            </a>
          </Button>
        )}
      </div>
    </section>
  )
}
